import { env } from '@/config/env';
import {
  VendorFilterState,
  VendorMetricsSummary,
  VendorRecord,
  VendorStatusKey,
} from '@/types/domain/vendor-management';
import { MOCK_VENDORS as CENTRAL_VENDORS } from '@/mocks/database';
import { processQuery, getEntityById } from './pagination';
import { notifyGlobal } from '@/context/NotificationContext';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_VENDOR_RECORDS: VendorRecord[] = CENTRAL_VENDORS.map((v, i) => ({
  id: i === 0 ? 'VND-LENOVO-01' : v.id,
  vendorCode: i === 0 ? 'VND-LENOVO-01' : v.vendorCode || `VND-${100 + i}`,
  sapVendorCode: i === 0 ? 'VND-SAP-8801' : v.sapVendorCode || `VND-SAP-${1000 + i}`,
  name: v.name,
  legalName: `${v.name} Corp`,
  vendorType: i % 3 === 0 ? 'OEM Manufacturer' : i % 2 === 0 ? 'Authorized Distributor' : 'Service Provider',
  category: v.category,
  industry: 'Information Technology & Industrial',
  status: (v.status === 'Active' ? 'APPROVED' : 'PENDING_REVIEW') as VendorStatusKey,
  verificationStatus: i % 10 === 0 ? 'PENDING' : 'VERIFIED',
  riskRating: 'LOW',
  primaryContact: {
    id: `CNT-${i + 1}`,
    name: v.contactPerson || `Contact Lead ${i + 1}`,
    role: 'Account Director',
    email: v.email,
    phone: v.phone,
    isPrimary: true,
    status: 'ACTIVE',
  },
  contacts: [
    {
      id: `CNT-${i + 1}`,
      name: v.contactPerson || `Contact Lead ${i + 1}`,
      role: 'Account Director',
      email: v.email,
      phone: v.phone,
      isPrimary: true,
      status: 'ACTIVE',
    },
  ],
  address: {
    street: v.address || '100 Enterprise Way',
    city: 'Austin',
    state: 'TX',
    country: 'USA',
    postalCode: '78701',
  },
  commercial: {
    currency: 'USD',
    paymentTerms: 'Net 30',
    incoterms: 'DDP',
    gstNumber: `GST${100000 + i}`,
    panNumber: `PAN${10000 + i}`,
    maskedBankAccount: `**** **** ${1000 + i}`,
    bankName: 'Enterprise Bank',
    preferredTier: 'STANDARD',
  },
  compliance: [
    {
      id: `CMP-${i + 1}`,
      documentType: 'ISO 9001 Quality Certificate',
      documentNumber: `ISO-${9000 + i}`,
      issuedDate: '2025-01-01',
      expiryDate: '2027-12-31',
      daysRemaining: 400,
      status: 'VERIFIED',
    },
  ],
  performance: {
    performanceScore: Math.round((v.rating || 4.5) * 20),
    onTimeDeliveryRate: 95 + (i % 5),
    qualityIndex: 96,
    rfqsParticipated: 12 + (i % 10),
    bidsSubmitted: 10 + (i % 8),
    ordersCompleted: 5 + (i % 4),
  },
  rfqHistory: [],
  createdAt: v.onboardedAt || '2026-01-01T09:00:00Z',
  updatedAt: v.onboardedAt || '2026-01-01T09:00:00Z',
  version: 1,
}));

export class MockVendorManagementService {
  private records: VendorRecord[] = [...GENERATED_VENDOR_RECORDS];

  async getVendorMetrics(): Promise<VendorMetricsSummary> {
    await delay(env.mockDelayMs);
    const totalVendors = this.records.length;
    const activeVendors = this.records.filter((r) => r.status === 'ACTIVE' || r.status === 'APPROVED').length;
    const pendingVerification = this.records.filter(
      (r) => r.status === 'PENDING_REVIEW' || r.verificationStatus === 'PENDING'
    ).length;
    const approvedVendors = this.records.filter((r) => r.status === 'APPROVED').length;
    const suspendedVendors = this.records.filter((r) => r.status === 'SUSPENDED').length;
    const blockedVendors = this.records.filter((r) => r.status === 'BLOCKED').length;
    const complianceExpiring = this.records.filter((r) =>
      r.compliance.some((c) => c.status === 'EXPIRED' || c.status === 'EXPIRING_SOON')
    ).length;
    const highRiskVendors = this.records.filter((r) => r.riskRating === 'HIGH' || r.riskRating === 'CRITICAL').length;

    return {
      totalVendors,
      activeVendors,
      pendingVerification,
      approvedVendors,
      suspendedVendors,
      blockedVendors,
      complianceExpiring,
      highRiskVendors,
    };
  }

  async getVendorRecords(filters?: Partial<VendorFilterState>): Promise<{ items: VendorRecord[]; total: number }> {
    await delay(env.mockDelayMs);
    const queryResult = processQuery(this.records, {
      page: filters?.page || 1,
      pageSize: filters?.pageSize || 25,
      search: filters?.search,
      filters: {
        status: filters?.status,
        category: filters?.category,
        riskRating: filters?.riskRating,
        verificationStatus: filters?.verificationStatus,
      },
    });

    return { items: queryResult.data, total: queryResult.total };
  }

  async getVendorById(id: string): Promise<VendorRecord | null> {
    await delay(env.mockDelayMs);
    const found = getEntityById(this.records, id, 'id') || getEntityById(this.records, id, 'vendorCode') || getEntityById(this.records, id, 'sapVendorCode');
    if (found) return found;
    if (this.records.length > 0) {
      const fallback = Object.assign({}, this.records[0], { id, vendorCode: id }) as VendorRecord;
      this.records.unshift(fallback);
      return fallback;
    }
    return null;
  }

  async checkDuplicateVendor(query: string): Promise<{ hasDuplicate: boolean; matches: VendorRecord[] }> {
    await delay(env.mockDelayMs);
    const q = query.trim().toLowerCase();
    const matches = this.records.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        v.legalName.toLowerCase().includes(q) ||
        (v.commercial && v.commercial.panNumber && v.commercial.panNumber.toLowerCase().includes(q))
    );
    const resultMatches: VendorRecord[] = matches.length > 0 ? matches : [this.records[0] as VendorRecord];
    return {
      hasDuplicate: matches.length > 0 || q.includes('lenovo') || q.includes('cisco'),
      matches: resultMatches,
    };
  }

  async updateVendorStatus(id: string, status: VendorStatusKey, _comments?: string): Promise<VendorRecord> {
    await delay(env.mockDelayMs);
    let vendor = await this.getVendorById(id);
    if (!vendor) {
      const fallback = Object.assign({}, this.records[0], { id, vendorCode: id }) as VendorRecord;
      this.records.unshift(fallback);
      vendor = fallback;
    }

    vendor.status = status;
    vendor.updatedAt = new Date().toISOString();
    notifyGlobal(
      `Vendor Status Updated: ${status}`,
      `Vendor ${vendor.name} (${vendor.vendorCode}) status changed to ${status}.`,
      'StatusChange',
      vendor.id
    );
    return vendor;
  }

  async createVendor(payload: Partial<VendorRecord>): Promise<VendorRecord> {
    await delay(env.mockDelayMs);
    const seq = Math.floor(100 + Math.random() * 900);
    const vendorCode = `VND-${seq}`;
    const vendorName = payload.name || payload.legalName || 'New Enterprise Vendor';

    const newRecord: VendorRecord = {
      id: vendorCode,
      vendorCode,
      sapVendorCode: `VND-SAP-${seq}`,
      name: vendorName,
      legalName: payload.legalName || vendorName,
      vendorType: payload.vendorType || 'Supplier',
      category: payload.category || 'IT Hardware',
      industry: payload.industry || 'Technology',
      status: 'PENDING_REVIEW',
      verificationStatus: 'PENDING',
      riskRating: 'MEDIUM',
      primaryContact: payload.primaryContact || {
        id: `CNT-${seq}`,
        name: 'Contact Manager',
        role: 'Account Representative',
        email: 'sales@vendor.enterprise',
        phone: '+1 555-0199',
        isPrimary: true,
        status: 'ACTIVE',
      },
      contacts: payload.contacts || [],
      address: payload.address || { street: '100 Enterprise Way', city: 'Austin', state: 'TX', country: 'USA', postalCode: '78701' },
      commercial: payload.commercial || {
        currency: 'USD',
        paymentTerms: 'Net 30',
        incoterms: 'DDP',
        gstNumber: 'GST100',
        panNumber: 'PAN100',
        maskedBankAccount: '**** 1000',
        bankName: 'HDFC',
        preferredTier: 'STANDARD',
      },
      compliance: payload.compliance || [],
      performance: payload.performance || {
        performanceScore: 85,
        onTimeDeliveryRate: 98,
        qualityIndex: 96,
        rfqsParticipated: 5,
        bidsSubmitted: 5,
        ordersCompleted: 2,
      },
      rfqHistory: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };

    this.records.unshift(newRecord);
    notifyGlobal(
      'Vendor Onboarded',
      `Vendor ${newRecord.name} (${newRecord.vendorCode}) added to master directory.`,
      'System',
      newRecord.id
    );
    return newRecord;
  }

  async updateVendor(id: string, data: Partial<VendorRecord>): Promise<VendorRecord> {
    await delay(env.mockDelayMs);
    const index = this.records.findIndex((r) => r.id === id || r.vendorCode === id);
    if (index === -1) throw new Error(`Vendor ${id} not found`);
    const current = this.records[index]!;
    const updated: VendorRecord = {
      ...current,
      ...data,
      version: (current.version || 1) + 1,
      updatedAt: new Date().toISOString(),
    };
    this.records[index] = updated;
    return updated;
  }

  async deleteVendor(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.records.length;
    const target = this.records.find((r) => r.id === id || r.vendorCode === id);
    this.records = this.records.filter((r) => r.id !== id && r.vendorCode !== id);
    if (target) {
      notifyGlobal(
        'Vendor Delisted',
        `Vendor ${target.name} (${target.vendorCode}) removed from master directory.`,
        'System'
      );
    }
    return this.records.length < initialLen;
  }
}

export const mockVendorManagementService = new MockVendorManagementService();
